import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, User, Shield, Users, FileText } from 'lucide-react';
import { useAuth, UserRole } from '@/contexts/AuthContext';

const roleDescriptions: Record<UserRole, { title: string; description: string; permissions: string[] }> = {
  creator: {
    title: 'Policy Creator',
    description: 'Create and manage draft policies',
    permissions: ['Create new policies', 'Edit draft policies', 'Submit for review']
  },
  reviewer: {
    title: 'Policy Reviewer',
    description: 'Review and provide feedback on policies',
    permissions: ['Review submitted policies', 'Add comments and suggestions', 'Approve or request changes']
  },
  approver: {
    title: 'Policy Approver',
    description: 'Final approval authority for policies',
    permissions: ['Approve reviewed policies', 'Publish approved policies', 'Archive outdated policies']
  },
  admin: {
    title: 'System Administrator',
    description: 'Full system access and user management',
    permissions: ['All creator, reviewer, and approver permissions', 'Manage users and roles', 'System configuration']
  },
  viewer: {
    title: 'Policy Viewer',
    description: 'View approved policies and use AI Agent',
    permissions: ['View approved policies only', 'Access AI Agent for questions', 'Read policy content']
  }
};

const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case 'creator': return <FileText className="h-6 w-6" />;
    case 'reviewer': return <Users className="h-6 w-6" />;
    case 'approver': return <CheckCircle className="h-6 w-6" />;
    case 'admin': return <Shield className="h-6 w-6" />;
    case 'viewer': return <User className="h-6 w-6" />;
  }
};

const getRoleColor = (role: UserRole) => {
  switch (role) {
    case 'creator': return 'bg-primary text-primary-foreground';
    case 'reviewer': return 'bg-warning text-warning-foreground';
    case 'approver': return 'bg-success text-success-foreground';
    case 'admin': return 'bg-destructive text-destructive-foreground';
    case 'viewer': return 'bg-muted text-muted-foreground';
  }
};

interface OnboardingProps {
  onComplete: () => void;
}

export const Onboarding = ({ onComplete }: OnboardingProps) => {
  const { profile, roles, company } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: `Welcome to ${company?.name || 'Manageaze'}, ${profile?.name}!`,
      content: (
        <div className="text-center space-y-4">
          <User className="h-16 w-16 mx-auto text-primary" />
          <p className="text-muted-foreground">
            {profile?.is_first_admin 
              ? "Congratulations! You're the first admin for your company. You have full access to manage policies and team members."
              : `Welcome to your company's policy management platform powered by Manageaze. You've been assigned specific roles to help manage organizational policies.`
            }
          </p>
          {!profile?.is_first_admin && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>You were invited by an admin!</strong> Your account has been set up with the appropriate permissions for your role.
              </p>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Your Role & Permissions',
      content: (
        <div className="space-y-6">
          {roles.length > 0 ? (
            roles.map((role) => {
              const roleInfo = roleDescriptions[role];
              return (
                <Card key={role} className="border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      {getRoleIcon(role)}
                      <div className="flex-1">
                        <CardTitle className="text-lg">{roleInfo.title}</CardTitle>
                        <CardDescription>{roleInfo.description}</CardDescription>
                      </div>
                      <Badge className={getRoleColor(role)}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Your permissions:</h4>
                      <ul className="space-y-1">
                        {roleInfo.permissions.map((permission, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            {permission}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="border-orange-200">
              <CardContent className="pt-6 text-center">
                <Shield className="h-12 w-12 mx-auto text-orange-500 mb-4" />
                <h3 className="font-semibold mb-2">Pending Role Assignment</h3>
                <p className="text-muted-foreground text-sm">
                  Your account is created but roles haven't been assigned yet. 
                  Please contact your administrator to assign appropriate roles.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )
    },
    {
      title: 'Your Company',
      content: (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">{company?.name}</h3>
            <p className="text-muted-foreground">
              You're now part of {company?.name}'s policy management system. 
              {profile?.is_first_admin 
                ? " As the admin, you can invite team members and manage the entire platform."
                : " You can collaborate with your team on policy creation, review, and approval processes."
              }
            </p>
          </div>
          {profile?.designation && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <strong>Your Designation:</strong> {profile.designation}
              </p>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Ready to Get Started!',
      content: (
        <div className="text-center space-y-4">
          <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
          <p className="text-muted-foreground">
            You're all set! You can now access the features available to your role. 
            Remember that access to certain features like draft policies depends on your assigned roles.
          </p>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-2xl">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center space-x-2 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full ${
                    index <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <CardTitle className="text-2xl">{steps[currentStep].title}</CardTitle>
            <CardDescription>
              Step {currentStep + 1} of {steps.length}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {steps[currentStep].content}
            
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                Previous
              </Button>
              <Button onClick={handleNext}>
                {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};